import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const HistoryContainer = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const RefreshControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #666;
`;

const AutoRefreshToggle = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.active ? '#28a745' : '#ddd'};
  border-radius: 20px;
  background: ${props => props.active ? '#d4edda' : '#f8f9fa'};
  color: ${props => props.active ? '#155724' : '#666'};
  cursor: pointer;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#c3e6cb' : '#e9ecef'};
  }
`;

const RefreshIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #888;
`;

const RefreshDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.active ? '#28a745' : '#ddd'};
  animation: ${props => props.active ? 'pulse 1.5s ease-in-out infinite' : 'none'};

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.8); }
  }
`;

const ManualRefreshButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #667eea;
  border-radius: 4px;
  background: ${props => props.disabled ? '#e9ecef' : '#667eea'};
  color: ${props => props.disabled ? '#999' : 'white'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.8rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #5a6fd6;
  }
`;

const Title = styled.h1`
  color: #333;
  font-size: 1.5rem;
`;

const StatsCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1.5rem;
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.9;
`;

const Breadcrumb = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 1.5rem;
`;

const FilterSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #555;
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.25);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  background-color: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.25);
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' && `
    background-color: #667eea;
    color: white;

    &:hover {
      background-color: #5a6fd6;
    }
  `}

  ${props => props.variant === 'secondary' && `
    background-color: #6c757d;
    color: white;

    &:hover {
      background-color: #5a6268;
    }
  `}

  ${props => props.variant === 'success' && `
    background-color: #28a745;
    color: white;

    &:hover {
      background-color: #218838;
    }
  `}

  ${props => props.variant === 'danger' && `
    background-color: #dc3545;
    color: white;

    &:hover {
      background-color: #c82333;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const CleanupSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CleanupInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const CleanupIcon = styled.span`
  font-size: 2rem;
`;

const CleanupText = styled.div``;

const CleanupTitle = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
`;

const CleanupDesc = styled.div`
  font-size: 0.875rem;
  color: #666;
`;

const CleanupActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  width: 400px;
  max-width: 90%;
`;

// 访客对话详情：头部与底部按钮固定，中间内容区超长时出滚动条
const DetailModalContent = styled(ModalContent)`
  width: 760px;
  max-width: 92vw;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1.75rem;
`;

const ModalBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 10px;
  margin-right: -10px;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c9c9c9;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const ModalFooterRow = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 1rem;
  margin-top: 0.75rem;
  border-top: 1px solid #eee;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #999;

  &:hover {
    color: #333;
  }
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  margin: 1rem 0;

  input {
    width: 18px;
    height: 18px;
  }
`;

const TableSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background-color: #f8f9fa;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #333;
  font-size: 0.875rem;
  border-bottom: 2px solid #dee2e6;
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  font-size: 0.875rem;
  color: #555;

  &:last-child {
    text-align: right;
  }
`;

const Tr = styled.tr`
  &:hover {
    background-color: #f8f9fa;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => {
    switch (props.status) {
      case 'pending': return '#fff3cd';
      case 'completed': return '#d4edda';
      case 'processing': return '#cce7ff';
      case 'cancelled': return '#f8d7da';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending': return '#856404';
      case 'completed': return '#155724';
      case 'processing': return '#0d47a1';
      case 'cancelled': return '#721c24';
      default: return '#383d41';
    }
  }};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  background-color: #f8f9fa;
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: #666;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: white;
  color: #333;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #e9ecef;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${props => props.active && `
    background-color: #667eea;
    color: white;
    border-color: #667eea;
  `}
`;

const EmptyState = styled.div`
  padding: 4rem;
  text-align: center;
  color: #666;
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  color: #333;
`;

const EmptyStateText = styled.p`
  font-size: 1rem;
`;

const DetailLabel = styled.div`
  font-weight: 600;
  color: #555;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`;

const DetailValue = styled.div`
  color: #333;
  font-size: 0.9rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  line-height: 1.5;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const DetailSection = styled.div`
  margin-bottom: 1.5rem;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const DetailItem = styled.div`
  margin-bottom: 1rem;
`;

const ResponseBox = styled.div`
  background: #f0fdf4;
  border-left: 4px solid #28a745;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
`;

const ConversationItem = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 1rem;
  flex-direction: ${props => props.isUser ? 'row-reverse' : 'row'};
`;

const ConversationAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.isUser ? '#667eea' : '#28a745'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
`;

const ConversationContent = styled.div`
  max-width: 70%;
  padding: 12px 16px;
  background: ${props => props.isUser ? '#667eea' : '#f0f9ff'};
  color: ${props => props.isUser ? 'white' : '#333'};
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
`;

const ConversationTime = styled.div`
  font-size: 11px;
  color: #999;
  margin-top: 4px;
  text-align: ${props => props.isUser ? 'right' : 'left'};
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ServiceRecordPage = () => {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    aiCount: 0,
    humanCount: 0,
    completedCount: 0,
    pendingCount: 0
  });
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    serviceType: 'all',
    status: 'all',
    search: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [autoCleanup, setAutoCleanup] = useState({ enabled: false, days: 30 });
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const navigate = useNavigate();

  // 获取客服记录列表
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        pageSize,
        ...(filters.serviceType !== 'all' && { serviceType: filters.serviceType }),
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.search && { search: filters.search }),
        ...(filters.dateFrom && { startTime: filters.dateFrom }),
        ...(filters.dateTo && { endTime: filters.dateTo })
      });

      const response = await axios.get(`/api/admin/service-records?${params.toString()}`);
      if (response.data.success) {
        setRecords(response.data.data.list);
        setTotal(response.data.data.pagination.total);
      }
    } catch (error) {
      console.error('获取客服记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取统计数据
  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/service-records/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
    }
  };

  // 获取自动清理设置
  const fetchCleanupSettings = async () => {
    try {
      const response = await axios.get('/api/admin/chat-records/auto-cleanup');
      if (response.data.success) {
        setAutoCleanup(response.data.data);
      }
    } catch (error) {
      console.error('获取清理设置失败:', error);
    }
  };

  // 保存自动清理设置
  const saveCleanupSettings = async () => {
    try {
      const response = await axios.post('/api/admin/chat-records/auto-cleanup', autoCleanup);
      if (response.data.success) {
        alert('清理设置已保存');
        setShowCleanupModal(false);
      }
    } catch (error) {
      console.error('保存清理设置失败:', error);
      alert('保存失败');
    }
  };

  // 手动清理
  const handleManualCleanup = async () => {
    if (!window.confirm(`确定要清理 ${autoCleanup.days} 天前的客服记录吗？`)) return;
    try {
      const response = await axios.delete(`/api/admin/chat-records/cleanup?days=${autoCleanup.days}`);
      if (response.data.success) {
        alert(response.data.message);
        fetchRecords();
        fetchStats();
      }
    } catch (error) {
      console.error('清理失败:', error);
      alert('清理失败');
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchStats();
    fetchCleanupSettings();
    setInitialLoading(false);
  }, [currentPage, filters]);

  // 自动刷新客服记录
  useEffect(() => {
    if (!autoRefresh || loading) return;

    const interval = setInterval(() => {
      fetchRecords();
      fetchStats();
      setLastUpdateTime(new Date());
    }, 10000); // 每10秒自动刷新

    return () => clearInterval(interval);
  }, [autoRefresh, loading, currentPage, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchRecords();
  };

  const handleReset = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      serviceType: 'all',
      status: 'all',
      search: ''
    });
    setCurrentPage(1);
  };

  // 一条记录 = 一个访客，详情拉取该访客的全部对话
  const handleViewDetails = async (userId) => {
    try {
      const response = await axios.get(`/api/admin/service-records/visitor/${encodeURIComponent(userId)}`);
      if (response.data.success) {
        setSelectedRecord(response.data.data);
        setShowModal(true);
      }
    } catch (error) {
      console.error('获取详情失败:', error);
      const msg = (error.response && error.response.data && error.response.data.message) || error.message;
      alert('获取访客对话失败：' + msg);
    }
  };

  // 删除某个访客的全部对话
  const handleDeleteVisitor = async (userId, userName) => {
    if (!window.confirm(`确定删除访客「${maskName(userName)}」的全部对话记录？此操作不可恢复。`)) return;
    try {
      const response = await axios.delete(`/api/admin/service-records/visitor/${encodeURIComponent(userId)}`);
      if (response.data.success) {
        alert(response.data.message || '已删除');
        setShowModal(false);
        fetchRecords();
        fetchStats();
      }
    } catch (error) {
      console.error('删除访客记录失败:', error);
      const msg = (error.response && error.response.data && error.response.data.message) || error.message;
      alert('删除失败：' + msg);
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);

  // 获取状态文本
  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return '待处理';
      case 'processing': return '处理中';
      case 'completed': return '已完成';
      case 'cancelled': return '已取消';
      default: return '未知';
    }
  };

  // 获取服务类型文本
  const getServiceTypeText = (type) => {
    switch (type) {
      case 'ai': return 'AI服务';
      case 'human': return '人工服务';
      default: return '未知';
    }
  };

  // 格式化时间
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('zh-CN');
  };

  // 脱敏用户名
  const maskName = (name) => {
    if (!name || name === '访客') return '访客';
    if (name.length <= 2) return name[0] + '*';
    return name[0] + '*'.repeat(name.length - 1);
  };

  if (initialLoading) {
    return (
      <AdminLayout title="客服记录">
        <HistoryContainer>
          <LoadingState>
            <LoadingSpinner />
          </LoadingState>
        </HistoryContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="客服记录">
      <HistoryContainer>
        <Breadcrumb>
          <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }}>仪表盘</a> / 客服记录
        </Breadcrumb>

        <Header>
          <HeaderLeft>
            <Title>客服记录</Title>
            <RefreshControl>
              <AutoRefreshToggle 
                active={autoRefresh} 
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                {autoRefresh ? '🔄 自动刷新' : '⏸️ 自动刷新'}
              </AutoRefreshToggle>
              <RefreshIndicator>
                <RefreshDot active={autoRefresh} />
                {lastUpdateTime ? `最后更新: ${lastUpdateTime.toLocaleTimeString('zh-CN')}` : '等待更新...'}
              </RefreshIndicator>
            </RefreshControl>
          </HeaderLeft>
          <ManualRefreshButton onClick={() => { fetchRecords(); fetchStats(); setLastUpdateTime(new Date()); }} disabled={loading}>
            {loading ? '刷新中...' : '🔄 立即刷新'}
          </ManualRefreshButton>
        </Header>

        {/* 统计卡片 */}
        <StatsCards>
          <StatCard style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>访客数</StatLabel>
          </StatCard>
          <StatCard style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <StatValue>{stats.totalMessages || 0}</StatValue>
            <StatLabel>对话消息</StatLabel>
          </StatCard>
          <StatCard style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <StatValue>{stats.aiCount}</StatValue>
            <StatLabel>AI 服务消息</StatLabel>
          </StatCard>
          <StatCard style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
            <StatValue>{stats.humanCount}</StatValue>
            <StatLabel>人工服务消息</StatLabel>
          </StatCard>
          <StatCard style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
            <StatValue>{stats.pendingCount}</StatValue>
            <StatLabel>待处理访客</StatLabel>
          </StatCard>
        </StatsCards>

        {/* 自动清理设置 */}
        <CleanupSection>
          <CleanupInfo>
            <CleanupIcon>🧹</CleanupIcon>
            <CleanupText>
              <CleanupTitle>自动清理设置</CleanupTitle>
              <CleanupDesc>
                {autoCleanup.enabled 
                  ? `已启用：自动清理 ${autoCleanup.days} 天前的记录` 
                  : '未启用自动清理'}
              </CleanupDesc>
            </CleanupText>
          </CleanupInfo>
          <CleanupActions>
            <Button variant="secondary" onClick={() => setShowCleanupModal(true)}>⚙️ 设置</Button>
            <Button variant="danger" onClick={handleManualCleanup}>🗑️ 手动清理</Button>
          </CleanupActions>
        </CleanupSection>

        <FilterSection>
          <FilterGrid>
            <FormGroup>
              <Label htmlFor="dateFrom">开始日期</Label>
              <Input
                type="date"
                id="dateFrom"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="dateTo">结束日期</Label>
              <Input
                type="date"
                id="dateTo"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="serviceType">服务类型</Label>
              <Select
                id="serviceType"
                name="serviceType"
                value={filters.serviceType}
                onChange={handleFilterChange}
              >
                <option value="all">全部</option>
                <option value="ai">AI服务</option>
                <option value="human">人工服务</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="status">状态</Label>
              <Select
                id="status"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="all">全部</option>
                <option value="pending">待处理</option>
                <option value="processing">处理中</option>
                <option value="completed">已完成</option>
                <option value="cancelled">已取消</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="search">搜索</Label>
              <Input
                type="text"
                id="search"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="搜索客户名称或消息内容"
              />
            </FormGroup>
          </FilterGrid>
          <ButtonGroup>
            <Button variant="primary" onClick={handleSearch}>搜索</Button>
            <Button variant="secondary" onClick={handleReset}>重置</Button>
          </ButtonGroup>
        </FilterSection>

        <TableSection>
          <Table>
            <thead>
              <tr>
                <Th>访客</Th>
                <Th>对话量</Th>
                <Th>最近提问</Th>
                <Th>服务类型</Th>
                <Th>状态</Th>
                <Th>最近时间</Th>
                <Th>操作</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <Td colSpan={7}>
                    <LoadingState>
                      <LoadingSpinner />
                    </LoadingState>
                  </Td>
                </tr>
              ) : records.length > 0 ? (
                records.map((item) => (
                  <Tr key={item.userId}>
                    <Td>
                      <div style={{ fontWeight: 500 }}>{maskName(item.userName)}</div>
                      <div style={{ fontSize: '0.72rem', color: '#999', marginTop: '2px' }}>
                        {item.ipAddress || '-'}
                      </div>
                    </Td>
                    <Td>
                      <div style={{ fontSize: '0.85rem' }}>{item.bubbleCount || 0} 条对话</div>
                      <div style={{ fontSize: '0.72rem', color: '#999' }}>
                        {item.recordCount || 0} 次提问
                      </div>
                    </Td>
                    <Td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.lastMessage}>
                      {item.lastMessage || '-'}
                    </Td>
                    <Td>
                      {item.serviceType === 'ai' ? '🤖 AI服务' : '👤 人工服务'}
                    </Td>
                    <Td>
                      <StatusBadge status={item.status}>
                        {getStatusText(item.status)}
                      </StatusBadge>
                    </Td>
                    <Td>{formatTime(item.lastAt)}</Td>
                    <Td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <Button variant="primary" onClick={() => handleViewDetails(item.userId)}>
                          查看对话
                        </Button>
                        <Button variant="danger" onClick={() => handleDeleteVisitor(item.userId, item.userName)}>
                          删除
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))
              ) : (
                <tr>
                  <Td colSpan={7}>
                    <EmptyState>
                      <EmptyStateIcon>📋</EmptyStateIcon>
                      <EmptyStateTitle>暂无客服记录</EmptyStateTitle>
                      <EmptyStateText>没有找到符合条件的数据</EmptyStateText>
                    </EmptyState>
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
          {!loading && records.length > 0 && (
            <Pagination>
              <PaginationInfo>
                显示 {startIndex + 1} 到 {endIndex} 共 {total} 位访客
              </PaginationInfo>
              <PaginationButtons>
                <PaginationButton
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  首页
                </PaginationButton>
                <PaginationButton
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  上一页
                </PaginationButton>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <PaginationButton
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      active={currentPage === pageNum}
                    >
                      {pageNum}
                    </PaginationButton>
                  );
                })}
                <PaginationButton
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  下一页
                </PaginationButton>
                <PaginationButton
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  末页
                </PaginationButton>
              </PaginationButtons>
            </Pagination>
          )}
        </TableSection>

        {showModal && selectedRecord && (
          <Modal onClick={() => setShowModal(false)}>
            <DetailModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>访客对话 · {maskName(selectedRecord.userName)}</ModalTitle>
                <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
              </ModalHeader>

              <ModalBody>
              <DetailGrid style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.75rem 1rem' }}>
                <DetailItem>
                  <DetailLabel>访客名称</DetailLabel>
                  <DetailValue>{maskName(selectedRecord.userName)}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>访客ID</DetailLabel>
                  <DetailValue style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>
                    {selectedRecord.userId || '-'}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>对话量</DetailLabel>
                  <DetailValue>
                    {(selectedRecord.conversation || []).length} 条对话 / {selectedRecord.recordCount || 0} 次提问
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>IP 地址</DetailLabel>
                  <DetailValue>{selectedRecord.ipAddress || '-'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>服务类型</DetailLabel>
                  <DetailValue>
                    <StatusBadge status={selectedRecord.serviceType === 'ai' ? 'completed' : 'pending'}>
                      {getServiceTypeText(selectedRecord.serviceType)}
                    </StatusBadge>
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>状态</DetailLabel>
                  <DetailValue>
                    <StatusBadge status={selectedRecord.status}>
                      {getStatusText(selectedRecord.status)}
                    </StatusBadge>
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>首次对话</DetailLabel>
                  <DetailValue>{formatTime(selectedRecord.firstAt)}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>最近对话</DetailLabel>
                  <DetailValue>{formatTime(selectedRecord.lastAt)}</DetailValue>
                </DetailItem>
              </DetailGrid>

              <DetailSection>
                <DetailLabel>全部对话（{(selectedRecord.conversation || []).length} 条）</DetailLabel>
                {selectedRecord.conversation && selectedRecord.conversation.length > 0 ? (
                  selectedRecord.conversation.map((msg, index) => (
                    <ConversationItem key={index} isUser={msg.role === 'user'}>
                      <ConversationAvatar isUser={msg.role === 'user'}>
                        {msg.role === 'user' ? '👤' : msg.role === 'agent' ? '👨‍💻' : '🤖'}
                      </ConversationAvatar>
                      <div>
                        <ConversationContent isUser={msg.role === 'user'}>
                          {msg.content}
                        </ConversationContent>
                        <ConversationTime isUser={msg.role === 'user'}>
                          {msg.timestamp}
                        </ConversationTime>
                      </div>
                    </ConversationItem>
                  ))
                ) : (
                  <DetailValue>暂无对话记录</DetailValue>
                )}
              </DetailSection>

              {selectedRecord.remark && (
                <DetailSection>
                  <DetailLabel>备注</DetailLabel>
                  <DetailValue>{selectedRecord.remark}</DetailValue>
                </DetailSection>
              )}
              </ModalBody>

              <ModalFooterRow>
                <Button variant="secondary" onClick={() => setShowModal(false)}>关闭</Button>
                <Button
                  variant="danger"
                  onClick={() => handleDeleteVisitor(selectedRecord.userId, selectedRecord.userName)}
                >
                  删除该访客全部对话
                </Button>
              </ModalFooterRow>
            </DetailModalContent>
          </Modal>
        )}

        {/* 清理设置弹窗 */}
        {showCleanupModal && (
          <ModalOverlay onClick={() => setShowCleanupModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>自动清理设置</ModalTitle>
                <CloseButton onClick={() => setShowCleanupModal(false)}>×</CloseButton>
              </ModalHeader>

              <FormGroup>
                <Label>启用自动清理</Label>
                <Toggle>
                  <input
                    type="checkbox"
                    checked={autoCleanup.enabled}
                    onChange={(e) => setAutoCleanup({ ...autoCleanup, enabled: e.target.checked })}
                  />
                  <span>开启后，将自动清理超过指定天数的客服记录</span>
                </Toggle>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="cleanupDays">清理天数</Label>
                <Input
                  type="number"
                  id="cleanupDays"
                  min="1"
                  max="365"
                  value={autoCleanup.days}
                  onChange={(e) => setAutoCleanup({ ...autoCleanup, days: parseInt(e.target.value) || 30 })}
                />
                <small style={{ color: '#666', fontSize: '0.8rem' }}>
                  清理多少天前的记录（1-365天）
                </small>
              </FormGroup>

              <ButtonGroup>
                <Button variant="primary" onClick={saveCleanupSettings}>保存设置</Button>
                <Button variant="secondary" onClick={() => setShowCleanupModal(false)}>取消</Button>
              </ButtonGroup>
            </ModalContent>
          </ModalOverlay>
        )}
      </HistoryContainer>
    </AdminLayout>
  );
};

export default ServiceRecordPage;
